import React, { useEffect, Suspense } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Box, Typography, Container, Grid, Button } from '@mui/material';
import {
  LocalShipping,
  RvHookup,
  ArrowForward,
  RequestQuote,
  VerifiedUser,
  Schedule,
  Bolt,
  Shield,
} from '@mui/icons-material';
import Carousel from '../components/Carousel';
import OptimizedImage from '../components/OptimizedImage';
import SectionLabel from '../components/SectionLabel';
import { LazyContactForm, LoadingFallback } from '../components/LazyComponents';
import { preloadImages } from '../utils/imageOptimization';
import { brand } from '../theme';

// Import images from src folder
import carouselTruck from '../images/carosel with truck.jpeg';
import cloudstruck from '../images/cloudstruck.jpeg';
import tankhaul from '../images/newtruck.png';
import twotruck from '../images/twotruckhaul.jpeg';
import nclogo from '../images/nclogo.jpg';

// The two conversion paths the whole site funnels toward.
const paths = [
  {
    icon: <LocalShipping sx={{ fontSize: 44 }} />,
    title: 'Hauling Services',
    blurb:
      'Hotshot, equipment, and vehicle hauling delivered on time — local or long-distance, secured and tracked.',
    cta: 'Explore Hauling',
    to: '/services',
    image: twotruck,
  },
  {
    icon: <RvHookup sx={{ fontSize: 44 }} />,
    title: 'Trailer Rentals',
    blurb:
      'Power tilt, car haulers, gravity tilt, and livestock trailers ready to rent. Browse the fleet and request a quote.',
    cta: 'View Trailers',
    to: '/rentals',
    image: cloudstruck,
  },
];

const trustPoints = [
  { icon: <VerifiedUser />, label: 'NC Certified' },
  { icon: <Bolt />, label: 'Hotshot Ready' },
  { icon: <Schedule />, label: 'On-Time Delivery' },
  { icon: <Shield />, label: 'Secured & Insured Loads' },
];

const Home = () => {
  const images = [carouselTruck, cloudstruck, tankhaul, twotruck];
  const location = useLocation();

  // Scroll to the rental quote form when navigated to with a hash (e.g. from the Rentals page)
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location.hash]);

  useEffect(() => {
    preloadImages([carouselTruck, cloudstruck, tankhaul, twotruck, nclogo]);

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'HDF Hauling',
      description:
        'Reliable hotshot, equipment, and vehicle hauling plus trailer rentals across the country',
      url: 'https://hdfhauling.com',
      logo: 'https://hdfhauling.com/static/media/logohdf.jpeg',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        areaServed: 'US',
        availableLanguage: 'English',
      },
      address: { '@type': 'PostalAddress', addressCountry: 'US' },
      sameAs: [],
      service: [
        {
          '@type': 'Service',
          name: 'Hauling Services',
          description:
            'Reliable freight delivery including hotshot loads, equipment, and vehicle transport',
        },
        {
          '@type': 'Service',
          name: 'Trailer Rentals',
          description:
            'Power tilt, car hauler, gravity tilt, and livestock trailer rentals for hotshot and freight hauling',
        },
        {
          '@type': 'Service',
          name: 'Fleet Management Services',
          description: 'Expert dispatching services with 24/7 support and personalized solutions',
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  return (
    <Box component="main" sx={{ background: brand.ink }}>
      {/* Hero */}
      <Box
        component="section"
        aria-label="HDF Hauling hero"
        sx={{
          position: 'relative',
          height: { xs: '78vh', sm: '70vh', md: '82vh' },
          minHeight: { xs: 480, md: 560 },
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0 }}>
          <Carousel images={images} height="100%" />
        </Box>

        {/* Scrim for legibility */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 3,
            background:
              'linear-gradient(90deg, rgba(11,11,13,0.92) 0%, rgba(11,11,13,0.72) 45%, rgba(11,11,13,0.25) 100%)',
          }}
        />

        {/* Hero content */}
        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 4,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ maxWidth: 720 }}>
            <SectionLabel align="left">North Carolina Certified · Hauling &amp; Rentals</SectionLabel>
            <Typography
              component="h1"
              variant="h1"
              sx={{
                color: 'white',
                fontSize: { xs: '2.6rem', sm: '3.4rem', md: '4.4rem' },
                lineHeight: 0.98,
                mb: 2.5,
              }}
            >
              Hauling Done Right.
              <Box component="span" sx={{ color: brand.redBright, display: 'block' }}>
                Trailers Ready to Roll.
              </Box>
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.82)',
                fontSize: { xs: '1.05rem', md: '1.25rem' },
                lineHeight: 1.6,
                maxWidth: 560,
                mb: 4,
              }}
            >
              From hotshot freight to equipment and vehicle transport — plus a fleet of quality
              trailers for rent. One trusted partner for everything you need to move.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Button
                component={Link}
                to="/#rental-quote"
                startIcon={<RequestQuote />}
                sx={{
                  px: 4,
                  py: 1.6,
                  fontSize: '1rem',
                  color: 'white',
                  background: `linear-gradient(45deg, ${brand.redDark}, ${brand.red})`,
                  boxShadow: '0 6px 20px rgba(225,20,20,0.45)',
                  '&:hover': {
                    background: `linear-gradient(45deg, ${brand.red}, ${brand.redBright})`,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.25s ease',
                }}
              >
                Get a Quote
              </Button>
              <Button
                component={Link}
                to="/rentals"
                endIcon={<ArrowForward />}
                sx={{
                  px: 4,
                  py: 1.6,
                  fontSize: '1rem',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.4)',
                  '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.08)' },
                }}
              >
                Browse Rentals
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Trust strip */}
      <Box sx={{ background: brand.red }}>
        <Container maxWidth="lg">
          <Grid container sx={{ py: { xs: 2, md: 2.5 } }}>
            {trustPoints.map((point) => (
              <Grid
                item
                xs={6}
                md={3}
                key={point.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.2,
                  py: 1.2,
                  color: 'white',
                }}
              >
                <Box sx={{ display: 'flex', '& svg': { fontSize: 26 } }}>{point.icon}</Box>
                <Typography
                  sx={{
                    fontFamily: '"Oswald", sans-serif',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: { xs: '0.8rem', md: '0.95rem' },
                  }}
                >
                  {point.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Two-path funnel */}
      <Box sx={{ py: { xs: 7, md: 11 } }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={{ xs: 5, md: 7 }}>
            <SectionLabel>How Can We Help?</SectionLabel>
            <Typography variant="h2" sx={{ color: 'white', fontSize: { xs: '2rem', md: '2.8rem' } }}>
              Two Ways to Get Moving
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 3, md: 4 }}>
            {paths.map((path) => (
              <Grid item xs={12} md={6} key={path.title}>
                <Box
                  component={Link}
                  to={path.to}
                  sx={{
                    display: 'block',
                    position: 'relative',
                    height: { xs: 320, md: 400 },
                    borderRadius: 3,
                    overflow: 'hidden',
                    textDecoration: 'none',
                    border: `1px solid ${brand.line}`,
                    '&:hover .path-img': { transform: 'scale(1.06)' },
                    '&:hover .path-arrow': { transform: 'translateX(6px)' },
                    '&:hover': { borderColor: `${brand.red}` },
                  }}
                >
                  <Box className="path-img" sx={{ position: 'absolute', inset: 0, transition: 'transform 0.5s ease' }}>
                    <OptimizedImage
                      src={path.image}
                      alt={path.title}
                      width="100%"
                      height="100%"
                      priority
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(180deg, rgba(11,11,13,0.35) 0%, rgba(11,11,13,0.55) 55%, rgba(11,11,13,0.95) 100%)',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'relative',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      p: { xs: 3, md: 4 },
                    }}
                  >
                    <Box sx={{ color: brand.redBright, mb: 1.5 }}>{path.icon}</Box>
                    <Typography variant="h3" sx={{ color: 'white', fontSize: { xs: '1.7rem', md: '2rem' }, mb: 1 }}>
                      {path.title}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, mb: 2, maxWidth: 440 }}>
                      {path.blurb}
                    </Typography>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'white',
                        fontFamily: '"Oswald", sans-serif',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                      }}
                    >
                      {path.cta}
                      <ArrowForward className="path-arrow" sx={{ fontSize: 20, transition: 'transform 0.25s ease' }} />
                    </Box>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* NC Certified band */}
      <Box sx={{ background: brand.surface, borderTop: `1px solid ${brand.line}`, borderBottom: `1px solid ${brand.line}` }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center" sx={{ py: { xs: 5, md: 7 } }}>
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <Box sx={{ maxWidth: 220, mx: 'auto' }}>
                <OptimizedImage
                  src={nclogo}
                  alt="North Carolina certified trucking company logo"
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <SectionLabel align="left">Why HDF Hauling</SectionLabel>
              <Typography variant="h3" sx={{ color: 'white', fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 2 }}>
                A North Carolina Certified Company
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.75, fontSize: { xs: '1rem', md: '1.1rem' } }}>
                We partner with our customers to get every load delivered safely and on time. Whether
                you need freight moved, equipment transported, or a reliable trailer for your own
                haul, HDF Hauling brings the right equipment and straightforward service to the job.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Quote form */}
      <Box id="rental-quote" sx={{ scrollMarginTop: '80px', py: { xs: 7, md: 11 } }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={{ xs: 4, md: 6 }}>
            <SectionLabel>Request a Quote</SectionLabel>
            <Typography variant="h2" sx={{ color: 'white', fontSize: { xs: '2rem', md: '2.8rem' }, mb: 2 }}>
              Get Your Free Quote
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.75)',
                maxWidth: 620,
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: { xs: '1rem', md: '1.1rem' },
              }}
            >
              Renting a trailer or need a load hauled? Tell us your tow vehicle, trailer type, load
              weight, and insurance info, and our team will get back to you within 24 hours.
            </Typography>
          </Box>

          <Suspense fallback={<LoadingFallback message="Loading form..." />}>
            <LazyContactForm />
          </Suspense>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
